import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Star, Plus, Trash2, Edit3, Save, X, Loader2 } from "lucide-react";

const EMPTY = { name: "", location: "", project_type: "", rating: 5, comment: "", published: true };

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // id or "new"
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/testimonials");
      setItems(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing("new"); setForm(EMPTY); setError(""); };
  const startEdit = (t) => { setEditing(t.id); setForm({ ...EMPTY, ...t }); setError(""); };
  const cancel = () => { setEditing(null); setForm(EMPTY); setError(""); };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        name: form.name.trim(),
        location: (form.location || "").trim() || null,
        project_type: (form.project_type || "").trim() || null,
        rating: Number(form.rating),
        comment: form.comment.trim(),
        published: !!form.published,
      };
      if (editing === "new") {
        await api.post("/admin/testimonials", payload);
      } else {
        await api.patch(`/admin/testimonials/${editing}`, payload);
      }
      await load();
      cancel();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet avis ? Cette action est définitive.")) return;
    try {
      await api.delete(`/admin/testimonials/${id}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) return <div className="py-10 text-center text-[#5C5A56]"><Loader2 className="inline animate-spin mr-2" size={16} /> Chargement…</div>;

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="text-sm text-[#5C5A56]">{items.length} avis au total</div>
        {editing === null && (
          <button
            onClick={startNew}
            data-testid="admin-testimonial-new"
            className="bg-[#C86641] text-[#FAFAFA] px-5 py-2.5 font-semibold inline-flex items-center gap-2 hover:bg-[#A65132] transition-colors"
          >
            <Plus size={16} /> Ajouter un avis
          </button>
        )}
      </div>

      {error && (
        <div data-testid="admin-testimonials-error" className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">{error}</div>
      )}

      {editing !== null && (
        <div data-testid="admin-testimonial-form" className="border border-[#E5E3DD] bg-[#F3F1ED] p-6 mb-6">
          <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#C86641] mb-4">
            {editing === "new" ? "Nouvel avis" : "Modifier l'avis"}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Nom du client *" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} testId="testi-name" />
            <Input label="Ville (optionnel)" value={form.location || ""} onChange={(v) => setForm((f) => ({ ...f, location: v }))} testId="testi-location" placeholder="Ex: Pontoise (95)" />
            <Input label="Type de projet (optionnel)" value={form.project_type || ""} onChange={(v) => setForm((f) => ({ ...f, project_type: v }))} testId="testi-project" placeholder="Ex: Rénovation cuisine" />
            <div>
              <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#5C5A56] mb-2">Note</div>
              <div className="flex gap-1" data-testid="testi-rating">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, rating: i }))}
                    data-testid={`testi-rating-${i}`}
                    className="p-1"
                  >
                    <Star size={28} className={i <= form.rating ? "fill-[#C86641] text-[#C86641]" : "text-[#E5E3DD]"} strokeWidth={1.5} />
                  </button>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#5C5A56] mb-2">Commentaire *</div>
              <textarea
                value={form.comment}
                onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
                rows={4}
                maxLength={600}
                data-testid="testi-comment"
                className="w-full bg-[#FAFAFA] px-4 py-3 border border-[#E5E3DD] focus:border-[#C86641] focus:outline-none transition-colors resize-none"
              />
              <div className="text-[11px] text-[#5C5A56] mt-1">{form.comment.length}/600</div>
            </div>
            <label className="md:col-span-2 inline-flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!form.published}
                onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
                data-testid="testi-published"
                className="w-4 h-4 accent-[#C86641]"
              />
              <span className="text-sm">Publier sur le site (visible publiquement)</span>
            </label>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={save}
              disabled={saving || !form.name.trim() || !form.comment.trim()}
              data-testid="testi-save"
              className="bg-[#1A1918] text-[#FAFAFA] px-6 py-3 font-semibold hover:bg-[#C86641] transition-colors inline-flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              Enregistrer
            </button>
            <button
              onClick={cancel}
              data-testid="testi-cancel"
              className="border border-[#1A1918] px-6 py-3 font-semibold hover:bg-[#1A1918] hover:text-[#FAFAFA] transition-colors inline-flex items-center gap-2"
            >
              <X size={16} /> Annuler
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.length === 0 ? (
          <div className="border border-[#E5E3DD] bg-[#FAFAFA] p-10 text-center text-[#5C5A56]">
            Aucun avis pour l'instant. Cliquez sur « Ajouter un avis » pour commencer.
          </div>
        ) : items.map((t) => (
          <div key={t.id} data-testid={`admin-testimonial-row-${t.id}`} className="border border-[#E5E3DD] bg-[#FAFAFA] p-5">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="font-semibold">{t.name}</div>
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} size={14} className={i <= t.rating ? "fill-[#C86641] text-[#C86641]" : "text-[#E5E3DD]"} strokeWidth={1.5} />
                    ))}
                  </div>
                  {!t.published && <span className="text-[10px] px-2 py-1 bg-amber-100 text-amber-700 font-bold uppercase tracking-wider">Brouillon</span>}
                </div>
                <div className="text-xs text-[#5C5A56] mt-1">{[t.project_type, t.location].filter(Boolean).join(" — ") || "—"}</div>
                <p className="mt-3 text-sm text-[#1A1918] leading-relaxed">« {t.comment} »</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => startEdit(t)}
                  data-testid={`admin-testimonial-edit-${t.id}`}
                  className="border border-[#E5E3DD] px-3 py-2 text-sm hover:bg-[#F3F1ED] inline-flex items-center gap-2"
                >
                  <Edit3 size={14} /> Modifier
                </button>
                <button
                  onClick={() => remove(t.id)}
                  data-testid={`admin-testimonial-delete-${t.id}`}
                  className="border border-red-200 text-red-700 px-3 py-2 text-sm hover:bg-red-50 inline-flex items-center gap-2"
                >
                  <Trash2 size={14} /> Supprimer
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testId, placeholder }) {
  return (
    <label className="block">
      <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#5C5A56] mb-2">{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-[#FAFAFA] px-4 py-3 border border-[#E5E3DD] focus:border-[#C86641] focus:outline-none transition-colors"
      />
    </label>
  );
}
